import { useEffect, useMemo, useState } from "react";
import {
    Button, Card, Col, DatePicker, Empty, Row, Select, Space, Statistic, Table, Tag, Typography,
} from "antd";
import {
    SafetyCertificateOutlined, FileExcelOutlined, FilePdfOutlined, ReloadOutlined, SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../variables/api/axiosInstance";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import { frenchDateOnly } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const PRIMARY = "#242166";

const fmt = (v) => `${displayNumbers(Math.trunc(Number(v) || 0))}`;

/**
 * « Listing des recours » (Sinistres) : tous les sinistres ayant une évaluation recours OU un
 * encaissement recours. Scopé côté serveur par organisme du dossier (le Pool voit tout). Filtres :
 * période (survenance) et « recours exercé contre » une compagnie du Pool. Export Excel / PDF.
 */
const ListingRecours = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [compagnies, setCompagnies] = useState([]); // [{value: code, label: raisonSociale}]
    const [range, setRange] = useState(null);
    const [compagnieContre, setCompagnieContre] = useState(undefined);

    const codeToNom = useMemo(() => {
        const m = {};
        compagnies.forEach((c) => {
            m[c.value] = c.label;
        });
        return m;
    }, [compagnies]);

    // Compagnies (organismes ASSURANCE) pour le filtre + traduction code → raison sociale.
    useEffect(() => {
        axiosInstance
            .get("/api/organisme/assurance")
            .then((res) => {
                const opts = (res?.data || [])
                    .filter((o) => o?.code)
                    .map((o) => ({ value: o.code, label: o.raisonSociale || o.code }));
                setCompagnies(opts);
            })
            .catch(() => toast.error("Erreur de chargement des compagnies"));
    }, []);

    const fetchListing = () => {
        setLoading(true);
        const params = {};
        if (range && range[0] && range[1]) {
            params.startDate = range[0].format("YYYY-MM-DD");
            params.endDate = range[1].format("YYYY-MM-DD");
        }
        if (compagnieContre) params.compagnieContre = compagnieContre;
        axiosInstance
            .get("/api/sinistre/recours/listing", { params })
            .then((res) => setData(res?.data || []))
            .catch(() => toast.error("Erreur lors du chargement du listing des recours"))
            .finally(() => setLoading(false));
    };

    // Chargement initial (sans filtre) + à chaque changement de filtre validé via le bouton.
    useEffect(() => {
        fetchListing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nomsRecours = (codes) =>
        (codes || []).map((c) => codeToNom[c] || c).filter(Boolean);

    // Prévision recours = évaluation recours − recours encaissé, PLAFONNÉE à 0 (un reste à encaisser
    // ne peut pas être négatif : un sur-recouvrement ⇒ reste nul).
    // ⚠️ On calcule EN DIRECT à partir de l'évaluation recours et de l'encaissé (tous deux frais côté
    // backend), et NON à partir du `sapRecours` PERSISTÉ : ce cache est parfois périmé (réévaluation
    // recours non répercutée), ce qui faisait diverger Σ Prévision de Σ Évaluation alors même qu'il
    // n'y a aucun encaissement (Σ encaissé = 0 ⇒ Prévision DOIT = Évaluation).
    const resteOf = (r) =>
        Math.max(0, (Number(r?.evaluationRecours) || 0) - (Number(r?.cumulEncaissementsRecours) || 0));

    // Totaux (pied de tableau + export).
    const totals = useMemo(() => {
        return (data || []).reduce(
            (acc, r) => {
                acc.evaluation += Number(r?.evaluationRecours) || 0;
                acc.encaisse += Number(r?.cumulEncaissementsRecours) || 0;
                acc.reste += resteOf(r);
                return acc;
            },
            { evaluation: 0, encaisse: 0, reste: 0 }
        );
    }, [data]);

    const columns = [
        {
            title: "N° sinistre",
            key: "num",
            render: (_, r) => r?.numeroSinistreM || r?.numeroSinistre || "-",
        },
        {
            title: "Assuré",
            key: "assure",
            render: (_, r) => `${r?.assureNom || ""} ${r?.assurePrenom || ""}`.trim() || "-",
        },
        {
            title: "Survenance",
            key: "survenance",
            render: (_, r) => (r?.dateSurvenance ? frenchDateOnly(r.dateSurvenance) : "-"),
        },
        {
            title: "Compagnie (dossier)",
            key: "orga",
            render: (_, r) => r?.organismeRaisonSociale || r?.organismeCode || "-",
        },
        {
            title: "Recours contre",
            key: "contre",
            render: (_, r) => {
                const noms = nomsRecours(r?.compagniesRecours);
                if (!noms.length) return <Text type="secondary">—</Text>;
                return (
                    <Space size={[0, 4]} wrap>
                        {noms.map((n) => (
                            <Tag key={n} color="blue">
                                {n}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: "Évaluation recours",
            key: "eval",
            align: "right",
            render: (_, r) => fmt(r?.evaluationRecours),
        },
        {
            title: "Recours encaissé",
            key: "enc",
            align: "right",
            render: (_, r) => fmt(r?.cumulEncaissementsRecours),
        },
        {
            title: "Prévision recours",
            key: "reste",
            align: "right",
            render: (_, r) => fmt(resteOf(r)),
        },
    ];

    // Lignes à plat pour les exports.
    const exportRows = () =>
        (data || []).map((r) => [
            r?.numeroSinistreM || r?.numeroSinistre || "",
            `${r?.assureNom || ""} ${r?.assurePrenom || ""}`.trim(),
            r?.dateSurvenance ? frenchDateOnly(r.dateSurvenance) : "",
            r?.organismeRaisonSociale || r?.organismeCode || "",
            nomsRecours(r?.compagniesRecours).join(", "),
            fmt(r?.evaluationRecours),
            fmt(r?.cumulEncaissementsRecours),
            fmt(resteOf(r)),
        ]);

    const periodeLabel = () =>
        range && range[0] && range[1]
            ? `du ${range[0].format("DD/MM/YYYY")} au ${range[1].format("DD/MM/YYYY")}`
            : "toutes périodes";

    const contreLabel = () => (compagnieContre ? codeToNom[compagnieContre] || compagnieContre : "toutes");

    const headers = [
        "N° sinistre", "Assuré", "Survenance", "Compagnie (dossier)", "Recours contre",
        "Évaluation recours", "Recours encaissé", "Prévision recours",
    ];

    const handleExcel = () => {
        if (!data.length) {
            toast.warn("Aucune donnée à exporter");
            return;
        }
        const meta = [
            ["ÉTAT DES RECOURS"],
            [`Période : ${periodeLabel()}`],
            [`Recours contre : ${contreLabel()}`],
            [],
        ];
        const totalRow = ["", "", "", "", "TOTAL", fmt(totals.evaluation), fmt(totals.encaisse), fmt(totals.reste)];
        const aoa = [...meta, headers, ...exportRows(), totalRow];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Recours");
        XLSX.writeFile(wb, `Etat_des_recours_${dayjs().format("YYYYMMDD")}.xlsx`);
    };

    const handlePdf = () => {
        if (!data.length) {
            toast.warn("Aucune donnée à exporter");
            return;
        }
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("ÉTAT DES RECOURS", 14, 15);
        doc.setFontSize(10);
        doc.text(`Période : ${periodeLabel()}`, 14, 22);
        doc.text(`Recours contre : ${contreLabel()}`, 14, 27);
        autoTable(doc, {
            startY: 32,
            head: [headers],
            body: exportRows(),
            foot: [["", "", "", "", "TOTAL", fmt(totals.evaluation), fmt(totals.encaisse), fmt(totals.reste)]],
            styles: { fontSize: 8 },
            headStyles: { fillColor: [36, 33, 102] },
            footStyles: { fillColor: [230, 230, 245], textColor: 20, fontStyle: "bold" },
        });
        doc.save(`Etat_des_recours_${dayjs().format("YYYYMMDD")}.pdf`);
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
            <Card
                style={{ marginBottom: 16, background: PRIMARY, borderRadius: 12 }}
                bodyStyle={{ padding: 20 }}
            >
                <Space align="center">
                    <SafetyCertificateOutlined style={{ fontSize: 26, color: "#fff" }} />
                    <div>
                        <Title level={4} style={{ color: "#fff", margin: 0 }}>
                            Listing des recours
                        </Title>
                        <Text style={{ color: "#e5e7eb" }}>
                            État des recours à exercer — dossiers avec évaluation ou encaissement de recours
                        </Text>
                    </div>
                </Space>
            </Card>

            <Card style={{ marginBottom: 16, borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                <Row gutter={[12, 12]} align="bottom">
                    <Col xs={24} md={9}>
                        <Text type="secondary">Période (survenance)</Text>
                        <RangePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={range}
                            onChange={setRange}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} md={9}>
                        <Text type="secondary">Recours exercé contre</Text>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Toutes les compagnies"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={compagnies}
                            value={compagnieContre}
                            onChange={setCompagnieContre}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={fetchListing} style={{ background: PRIMARY }}>
                                Rechercher
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    setRange(null);
                                    setCompagnieContre(undefined);
                                    setTimeout(fetchListing, 0);
                                }}
                            >
                                Réinitialiser
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8}>
                    <Card bordered style={{ borderRadius: 12 }}>
                        <Statistic title="Évaluation recours (Σ)" value={fmt(totals.evaluation)} suffix="FCFA" valueStyle={{ color: PRIMARY }} />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card bordered style={{ borderRadius: 12 }}>
                        <Statistic title="Recours encaissé (Σ)" value={fmt(totals.encaisse)} suffix="FCFA" valueStyle={{ color: "#389e0d" }} />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card bordered style={{ borderRadius: 12 }}>
                        <Statistic title="Prévision recours (Σ)" value={fmt(totals.reste)} suffix="FCFA" valueStyle={{ color: "#d46b08" }} />
                    </Card>
                </Col>
            </Row>

            <Card
                style={{ borderRadius: 12 }}
                bodyStyle={{ padding: 12 }}
                title={<Text strong>{`${data.length} dossier(s)`}</Text>}
                extra={
                    <Space>
                        <Button icon={<FileExcelOutlined />} onClick={handleExcel} disabled={!data.length}>
                            Excel
                        </Button>
                        <Button icon={<FilePdfOutlined />} onClick={handlePdf} disabled={!data.length}>
                            PDF
                        </Button>
                    </Space>
                }
            >
                <Table
                    size="small"
                    rowKey={(r) => r?.sinistreCodeId}
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    scroll={{ x: "max-content" }}
                    locale={{ emptyText: <Empty description="Aucun recours" /> }}
                    pagination={{ pageSize: 25, showSizeChanger: true }}
                    summary={() =>
                        data.length ? (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={5}>
                                        <Text strong>TOTAL</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        <Text strong>{fmt(totals.evaluation)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">
                                        <Text strong style={{ color: "#389e0d" }}>{fmt(totals.encaisse)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} align="right">
                                        <Text strong style={{ color: "#d46b08" }}>{fmt(totals.reste)}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        ) : null
                    }
                />
            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListingRecours;
